import React, { useRef, useEffect, useState } from 'react';
import { Link } from '../i18n/LocaleLink';
import { useGSAP } from '../hooks/useGSAP';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitText from './SplitText';
import { CardContainer, CardBody, CardItem } from './ui/3d-card';
import { useLocalizedServices } from '../i18n/localize';
import { useLocale } from '../i18n/LocaleContext';

// Define animation props outside component to prevent re-renders
const splitTextFrom = { opacity: 0, y: 40 };
const splitTextTo = { opacity: 1, y: 0 };

// Per-card signature accent — gives the otherwise-identical dark cards their
// own colour identity (web=periwinkle, motion=coral, branding=butter, logo=orange).
const SERVICE_ACCENTS = ['#b1c1ef', '#f2acac', '#ffdd94', '#f97b22'];

const ServicesSection = () => {
  const { t } = useLocale();
  const services = useLocalizedServices();
  const gsap = useGSAP();
  const sectionRef = useRef(null);

  const [clickedServices, setClickedServices] = useState(new Set());

  useEffect(() => {
    if (!gsap || !sectionRef.current) return;

    gsap.registerPlugin(ScrollTrigger);

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, [gsap]);

  const handleCoinClick = (e, serviceId) => {
    e.preventDefault();
    e.stopPropagation();
    setClickedServices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };

  return (
    <div ref={sectionRef} className="bg-[#f2f3d9] relative py-20 sm:py-24 lg:py-32">
      <div className="max-w-[1422px] mx-auto px-5 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col gap-8 items-start justify-start mb-16 sm:mb-20 lg:mb-32">
          <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between w-full gap-8">
            <div className="font-anton text-[18vw] sm:text-[140px] lg:text-[200px] text-mainkleur leading-[0.9] tracking-[-4px] sm:tracking-[-6px] lg:tracking-[-8px] w-full lg:w-[761px]">
              <SplitText
                text={t('home.servicesHeading')}
                splitType="words"
                delay={300}
                duration={1.8}
                ease="power3.out"
                from={splitTextFrom}
                to={splitTextTo}
                threshold={0}
                rootMargin={"+100px"}
              />
            </div>

            <div className="font-space font-normal text-[15px] sm:text-[17px] lg:text-[20px] text-mainkleur text-left lg:text-right w-full lg:w-[638px]">
              <p className="leading-[1.35] sm:leading-[1.2] lg:leading-[0.907]">
                {t('home.servicesBody')}
              </p>
            </div>
          </div>

          <div className="w-full h-px bg-mainkleur opacity-30"></div>
        </div>

        {/* Bento Grid Services */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-8xl mx-auto">
          {services.map((service, index) => {
            const accent = SERVICE_ACCENTS[index % SERVICE_ACCENTS.length];
            return (
            <CardContainer key={service.id} className="w-full h-full" containerClassName="h-full">
              <CardBody
                className="bg-[#262624] backdrop-blur-sm border border-white rounded-2xl p-6 sm:p-8 lg:p-10 pb-16 sm:pb-[4.5rem] lg:pb-20 hover:shadow-2xl hover:shadow-white/10 transition-all duration-500 group relative overflow-hidden h-[410px] lg:h-[490px] w-full"
              >
                {/* Accent glow that blooms from the top edge on hover */}
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-24 -right-16 w-56 h-56 rounded-full blur-[80px] opacity-30 group-hover:opacity-60 transition-opacity duration-500"
                  style={{ backgroundColor: accent }}
                />

                {/* Service Icon and Title */}
                <CardItem translateZ="20" className="flex items-center gap-3 sm:gap-3.5 lg:gap-4 mb-4 sm:mb-5 lg:mb-6 relative z-10">
                  <div
                    className="flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl text-2xl sm:text-3xl lg:text-4xl group-hover:scale-110 transition-transform duration-300"
                    style={{ backgroundColor: `${accent}26`, boxShadow: `inset 0 0 0 1px ${accent}66` }}
                  >
                    {service.icon}
                  </div>
                  <h3 className="font-bricolage font-extrabold text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight group-hover:text-white/90 transition-colors duration-300">
                    {service.title}
                  </h3>
                </CardItem>

                {/* Divider line between title and tags — tinted with the card accent */}
                <CardItem translateZ="10" className="w-full h-px mb-4 sm:mb-5 lg:mb-6 relative z-10" style={{ backgroundColor: `${accent}59` }}></CardItem>

                {/* Description */}
                <CardItem translateZ="30" className="text-white/80 text-sm sm:text-base lg:text-lg mb-4 sm:mb-5 lg:mb-6 leading-relaxed relative z-10">
                  {service.description}
                </CardItem>

                {/* Software Tags */}
                <CardItem translateZ="40" className="flex flex-wrap gap-2 sm:gap-2.5 lg:gap-3 mb-4 sm:mb-5 lg:mb-6 relative z-10">
                  {service.tools.map((tool) => (
                    <span
                      key={tool.name}
                      className="bg-white/10 text-white/90 px-3 py-1.5 sm:px-3.5 sm:py-1.5 lg:px-4 lg:py-2 rounded-lg text-sm sm:text-base lg:text-lg font-medium border border-white/20 group-hover:bg-white/20 group-hover:scale-105 transition-all duration-300"
                    >
                      {tool.name}
                    </span>
                  ))}

                  {/* Cost tag - appears when clicked with smooth fade in/out */}
                  {clickedServices.has(service.id) && (
                    <span
                      className="text-mainkleur px-3 py-1.5 sm:px-3.5 sm:py-1.5 lg:px-4 lg:py-2 rounded-lg text-sm sm:text-base lg:text-lg font-semibold group-hover:scale-105 transition-all duration-500 ease-in-out price-tag-enter"
                      style={{ backgroundColor: accent, boxShadow: `inset 0 0 0 1px ${accent}` }}
                    >
                      {service.cost}
                    </span>
                  )}
                </CardItem>

                {/* Bottom-right action row: Discuss this CTA + coin price toggle */}
                <CardItem
                  translateZ="50"
                  className="absolute bottom-3 right-3 sm:bottom-3.5 sm:right-3.5 lg:bottom-4 lg:right-4 z-20 flex items-center gap-2 sm:gap-2.5"
                >
                  <Link
                    to={`/contact?service=${encodeURIComponent(service.slug)}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 sm:gap-2 bg-[#f97b22] hover:bg-[#fe9043] text-mainkleur rounded-full px-3.5 py-2 sm:px-4 sm:py-2.5 font-bricolage font-semibold text-xs sm:text-sm uppercase tracking-tight hover:scale-[1.05] transition-all duration-300 shadow-lg shadow-[#f97b22]/30 whitespace-nowrap"
                    title={`Discuss ${service.title}`}
                  >
                    <span>{t('home.discussThis')}</span>
                    <svg
                      className="w-3.5 h-3.5 sm:w-4 sm:h-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </Link>

                  <button
                    type="button"
                    onClick={(e) => handleCoinClick(e, service.id)}
                    className="bg-white/10 hover:bg-white/20 text-white/90 p-2.5 sm:p-2.5 lg:p-3 rounded-full border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-110 hover:rotate-12 cursor-pointer w-10 h-10 sm:w-11 sm:h-11 lg:w-12 lg:h-12 flex items-center justify-center"
                    aria-label={clickedServices.has(service.id) ? t('home.hidePrice') : t('home.showPrice')}
                    aria-pressed={clickedServices.has(service.id)}
                  >
                    <svg className="w-5 h-5 sm:w-5.5 sm:h-5.5 lg:w-6 lg:h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z" clipRule="evenodd" />
                    </svg>
                  </button>
                </CardItem>
              </CardBody>
            </CardContainer>
            );
          })}
        </div>

        {/* Link to detailed services page */}
        <div className="mt-12 sm:mt-14 lg:mt-16 flex justify-center">
          <Link
            to="/services"
            className="group inline-flex items-center gap-3 sm:gap-4 font-bricolage font-extrabold text-mainkleur text-[22px] sm:text-[28px] lg:text-[32px] uppercase tracking-tight hover:text-[#f97b22] transition-colors duration-300"
          >
            <span>{t('home.seeBreakdown')}</span>
            <span className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-mainkleur group-hover:bg-mainkleur group-hover:text-licht flex items-center justify-center transition-all duration-300 group-hover:rotate-[-45deg]">
              <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ServicesSection;
